import {
  GetIssueResponseDtoStatusEnum,
  IssueSubtypesManagedByMeDto,
  IssueTypesManagedByMeDto,
  ManagedIssuesDto,
} from '@issuemanager/api-client';
import {
  DEFAULT_FILTER_MODEL_STATE,
  TransformedIssueSLAsDto,
  useDataGridContext,
} from '@issuemanager/apps/client/components';
import { useIssueStatuses } from '@issuemanager/client/features/issues';
import {
  IssueActionProgressEnum,
  ManagedIssuesTypeEnum,
  SLAStatusEnum,
} from '@issuemanager/constants';
import {
  ManagedIssuesEnum,
  QuickFilterAllEnum,
} from '@issuemanager/test/constants';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIssueActionProgress } from '../issue-action-progress-label';
import { QuickFilterContainer } from './quick-filter-container';
import { QuickFilterItem } from './quick-filter-item';

export function ManagedIssues({
  managedIssues,
  slaDueDays,
  issueTypesManagedByMe,
  issueSubtypesManagedByMe,
  activeFilter,
  setActiveFilter,
}: {
  managedIssues: ManagedIssuesDto | undefined;
  slaDueDays: Array<number> | undefined;
  issueTypesManagedByMe: Array<IssueTypesManagedByMeDto> | undefined;
  issueSubtypesManagedByMe: Array<IssueSubtypesManagedByMeDto> | undefined;
  activeFilter: QuickFilterAllEnum | null;
  setActiveFilter: (value: QuickFilterAllEnum) => void;
}) {
  const context = useDataGridContext();
  const actionProgress = useIssueActionProgress();
  const { t } = useTranslation('issue');
  const { issueStatuses } = useIssueStatuses();

  const clearFilters = () => {
    context.setFilterModel(DEFAULT_FILTER_MODEL_STATE);
  };

  const issueTypes = useMemo(
    () =>
      issueTypesManagedByMe?.map((issueType) => ({
        id: issueType.id,
        name: issueType.name,
      })),
    [issueTypesManagedByMe],
  );

  const issueSubtypes = useMemo(
    () =>
      issueSubtypesManagedByMe?.map((issueSubtype) => ({
        id: issueSubtype.id,
        name: issueSubtype.name,
      })),
    [issueSubtypesManagedByMe],
  );

  const actionProgressNotInitiated = useMemo(
    () =>
      actionProgress.find(
        (a) => a.value === IssueActionProgressEnum.NOT_INITIATED,
      ),
    [actionProgress],
  );
  const actionProgressInitiated = useMemo(
    () =>
      actionProgress.find((a) => a.value === IssueActionProgressEnum.INITIATED),
    [actionProgress],
  );
  const issueStatusOpen = useMemo(
    () =>
      issueStatuses.find(
        (s) => s.statusValue === GetIssueResponseDtoStatusEnum.Open,
      ),
    [issueStatuses],
  );

  const slaDays: TransformedIssueSLAsDto[] =
    slaDueDays?.map((day) => ({
      id: String(day),
      slaDueDays: day,
    })) ?? [];

  const handleFilterSelection = (type: string) => {
    clearFilters();

    const excludeDiscarded = issueStatuses.filter(
      (s) => s.statusValue !== GetIssueResponseDtoStatusEnum.Discarded,
    );
    const excludeDiscardedAndClosed = issueStatuses.filter(
      (s) =>
        s.statusValue !== GetIssueResponseDtoStatusEnum.Discarded &&
        s.statusValue !== GetIssueResponseDtoStatusEnum.Closed,
    );

    switch (type) {
      case ManagedIssuesTypeEnum.ALL:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.IN_REVIEW:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
            status: [issueStatusOpen],
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.ACTIONS_NOT_INITIATED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
          actionProgress: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
            actionProgress: [actionProgressNotInitiated],
            status: excludeDiscarded,
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.ACTIONS_INITIATED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
          actionProgress: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
            actionProgress: [actionProgressInitiated],
            status: excludeDiscarded,
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.OVERDUE:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
          dueDate: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
            dueDate: [null, dayjs(new Date())],
            status: excludeDiscardedAndClosed,
          }),
          'quick-filter',
        );
        break;
      case ManagedIssuesTypeEnum.SLA_OVERDUE:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
          slaDueDays: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
            slaDueDays: slaDays,
            slaStatus: [SLAStatusEnum.OVERDUE],
            status: excludeDiscardedAndClosed,
          }),
          'quick-filter',
        );
        break;
    }
  };

  return (
    <QuickFilterContainer label={t('quickFilters.managedIssues.title')}>
      <QuickFilterItem
        label={t('quickFilters.managedIssues.all')}
        number={managedIssues?.all}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.ALL);
          setActiveFilter(ManagedIssuesEnum.ALL);
        }}
        isActive={activeFilter === ManagedIssuesEnum.ALL}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.inReview')}
        number={managedIssues?.open}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.IN_REVIEW);
          setActiveFilter(ManagedIssuesEnum.IN_REVIEW);
        }}
        isActive={activeFilter === ManagedIssuesEnum.IN_REVIEW}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.actionsNotInitiated')}
        number={managedIssues?.notInitiated}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.ACTIONS_NOT_INITIATED);
          setActiveFilter(ManagedIssuesEnum.ACTIONS_NOT_INITIATED);
        }}
        isActive={activeFilter === ManagedIssuesEnum.ACTIONS_NOT_INITIATED}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.actionsInitiated')}
        number={managedIssues?.initiated}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.ACTIONS_INITIATED);
          setActiveFilter(ManagedIssuesEnum.ACTIONS_INITIATED);
        }}
        isActive={activeFilter === ManagedIssuesEnum.ACTIONS_INITIATED}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.overdue')}
        number={managedIssues?.overdue}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.OVERDUE);
          setActiveFilter(ManagedIssuesEnum.OVERDUE);
        }}
        isActive={activeFilter === ManagedIssuesEnum.OVERDUE}
      />
      <QuickFilterItem
        label={t('quickFilters.managedIssues.slaOverdue')}
        number={managedIssues?.slaOverdue}
        onClick={() => {
          handleFilterSelection(ManagedIssuesTypeEnum.SLA_OVERDUE);
          setActiveFilter(ManagedIssuesEnum.SLA_OVERDUE);
        }}
        isActive={activeFilter === ManagedIssuesEnum.SLA_OVERDUE}
      />
    </QuickFilterContainer>
  );
}
